import type React from 'react';
import {Box, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import type {ExternalSignals} from '../../types/signalsTypes';
import {ProfileSection} from '../components/ProfileSection';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: 'numeric', month: 'short', year: 'numeric'});
const FRESH = {fresh: ['Live', 'success'], stale: ['Stale', 'warning'], empty: ['No data', 'default']} as const;

type Row = {key: string; source: string; type: string; provider: string; status: [string, 'success' | 'warning' | 'default' | 'info'];
    refresh: string | null; usedBy: string};

/** Category source registry (spec 4.12): every price index plus the event feed and category risk. */
export const SignalSourceRegistry: React.FC<{data: ExternalSignals}> = ({data}) => {
    const p = data.provider;
    const feed: [string, 'success' | 'info'] = p.enabled ? ['Live', 'success'] : ['Manual entries only', 'info'];
    const rows: Row[] = [
        {key: 'events', source: 'Event feed', type: 'Signals', provider: p.name, status: feed, refresh: p.lastRefresh,
            usedBy: `${p.manualSignals} added by Mithra`},
        {key: 'risk', source: 'Category risk', type: 'Risk score', provider: 'Computed from signals', status: feed,
            refresh: null, usedBy: 'This category'},
        ...data.registry.map(s => ({
            key: `series-${s.id}`, source: s.name, type: 'Price index', provider: `${s.provider} · ${s.tier}`,
            status: [...FRESH[s.freshness]] as Row['status'], refresh: s.lastRefreshAt,
            usedBy: s.usedBy ? `${s.usedBy} categor${s.usedBy === 1 ? 'y' : 'ies'}` : '—',
        })),
    ];
    return (
        <ProfileSection title="Category source registry" subtitle={`${rows.length} sources`}>
            <Box sx={{overflowX: 'auto'}}>
                <Table size="small" aria-label="Category source registry">
                    <TableHead>
                        <TableRow>
                            <TableCell>Source</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Last refresh</TableCell>
                            <TableCell>Used by</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => (
                            <TableRow key={r.key}>
                                <TableCell><Typography variant="body2">{r.source}</Typography></TableCell>
                                <TableCell>{r.type}</TableCell>
                                <TableCell>{r.provider}</TableCell>
                                <TableCell><Tag label={r.status[0]} color={r.status[1]} solid={false}/></TableCell>
                                <TableCell>{r.refresh ? WHEN.format(new Date(r.refresh)) : '—'}</TableCell>
                                <TableCell>{r.usedBy}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </ProfileSection>
    );
};
